import {
  ChangeDetectionStrategy,
  Component,
  computed,
  forwardRef,
  input,
  signal,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

/**
 * Larger touch-friendly checkbox with {@link ControlValueAccessor} support.
 *
 * Use with reactive forms / `ngModel`, or one-way `[ngModel]` + `(ngModelChange)`.
 * Safe inside a parent `<label>` (does not nest labels).
 */
@Component({
  selector: 'rind-checkbox',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CheckboxComponent),
      multi: true,
    },
  ],
  host: {
    class: 'rind-checkbox',
    '[class.rind-checkbox--checked]': 'checked()',
    '[class.rind-checkbox--disabled]': 'isDisabled()',
  },
  template: `
    <span class="rind-checkbox__control">
      <input
        type="checkbox"
        class="rind-checkbox__input"
        [checked]="checked()"
        [disabled]="isDisabled()"
        [attr.name]="name() || null"
        [attr.id]="inputId() || null"
        [attr.aria-label]="ariaLabel() || null"
        [attr.aria-labelledby]="ariaLabelledby() || null"
        (change)="onInputChange($event)"
        (blur)="onBlur()"
      />
      <span class="rind-checkbox__box" aria-hidden="true">
        <svg class="rind-checkbox__mark" viewBox="0 0 16 16" fill="none">
          <path
            d="M3.5 8.2 6.6 11.3 12.5 4.7"
            stroke="currentColor"
            stroke-width="2.2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </span>
    </span>
  `,
  styles: `
    :host {
      display: inline-flex;
      align-items: center;
      vertical-align: middle;
      flex-shrink: 0;
      line-height: 0;
    }

    .rind-checkbox__control {
      position: relative;
      display: inline-flex;
      width: var(--rind-checkbox-size, 1.375rem);
      height: var(--rind-checkbox-size, 1.375rem);
    }

    .rind-checkbox__input {
      position: absolute;
      inset: 0;
      z-index: 1;
      margin: 0;
      padding: 0;
      opacity: 0;
      cursor: pointer;
      width: 100%;
      height: 100%;
    }

    .rind-checkbox__box {
      box-sizing: border-box;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      height: 100%;
      border: 2px solid var(--color-border-strong, #9aa3ae);
      border-radius: var(--radius-sm, 4px);
      background: var(--color-surface, #fff);
      color: var(--color-primary-contrast, #fff);
      transition:
        background-color 120ms ease,
        border-color 120ms ease,
        box-shadow 120ms ease;
    }

    .rind-checkbox__mark {
      width: 0.875rem;
      height: 0.875rem;
      opacity: 0;
      transform: scale(0.85);
      transition:
        opacity 100ms ease,
        transform 100ms ease;
    }

    .rind-checkbox__input:checked + .rind-checkbox__box {
      background: var(--color-primary, #f28c28);
      border-color: var(--color-primary, #f28c28);
    }

    .rind-checkbox__input:checked + .rind-checkbox__box .rind-checkbox__mark {
      opacity: 1;
      transform: scale(1);
    }

    .rind-checkbox__input:hover:not(:disabled) + .rind-checkbox__box {
      border-color: var(--color-primary, #f28c28);
    }

    .rind-checkbox__input:focus-visible + .rind-checkbox__box {
      outline: 2px solid var(--color-focus, #20486f);
      outline-offset: 2px;
    }

    :host.rind-checkbox--disabled {
      opacity: 0.55;
    }

    .rind-checkbox__input:disabled {
      cursor: not-allowed;
    }
  `,
})
export class CheckboxComponent implements ControlValueAccessor {
  /** Native `name` for form posts / autofill grouping. */
  readonly name = input<string | null>(null);
  /** Optional id for associating external labels via `for`. */
  readonly inputId = input<string | null>(null, { alias: 'id' });
  readonly ariaLabel = input<string | null>(null, { alias: 'aria-label' });
  readonly ariaLabelledby = input<string | null>(null, { alias: 'aria-labelledby' });
  /** Disabled from the template; combined with CVA `setDisabledState`. */
  readonly disabled = input(false);

  protected readonly checked = signal(false);
  private readonly cvaDisabled = signal(false);

  protected readonly isDisabled = computed(() => this.disabled() || this.cvaDisabled());

  private onChange: (value: boolean) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  writeValue(value: unknown): void {
    this.checked.set(value === true);
  }

  registerOnChange(fn: (value: boolean) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.cvaDisabled.set(isDisabled);
  }

  protected onInputChange(event: Event): void {
    const next = (event.target as HTMLInputElement).checked;
    this.checked.set(next);
    this.onChange(next);
  }

  protected onBlur(): void {
    this.onTouched();
  }
}
